<template>
  <div class="private-fund-order" :class="bgColor">
    <van-nav-bar
      v-show="!isApp"
      class="private-fund-order__nav"
      :title="$t('privateFund.order.title')"
      left-arrow
      @click-left="handleBack"
    />

    <div ref="listScroll" class="private-fund-order__body">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <div v-if="showEmpty" class="private-fund-order__empty">
          <img
            class="private-fund-order__empty-icon"
            src="@/assets/images/app-pages/recordEmpty.png"
            alt=""
          />
          <span class="private-fund-order__empty-text">{{ $t("public.contentText[1]") }}</span>
        </div>

        <template v-else>
          <div class="private-fund-order__table-head">
            <span class="private-fund-order__th is-name">{{ $t("privateFund.order.colName") }}</span>
            <div class="private-fund-order__th-group">
              <span class="private-fund-order__th">
                {{ $t("privateFund.order.colAmount") }} | {{ $t("privateFund.order.colCurrency") }}
              </span>
              <span class="private-fund-order__th">
                {{ $t("privateFund.order.colStatus") }}/{{ $t("privateFund.order.colTime") }}
              </span>
            </div>
          </div>

          <van-list
            v-model="loading"
            :finished="finished"
            :immediate-check="false"
            offset="80"
            :finished-text="$t('privateFund.finishedText')"
            @load="onLoad"
          >
            <div
              v-for="item in list"
              :key="item.id || item.orderNo"
              class="private-fund-order__row"
            >
              <p class="private-fund-order__name">{{ item.productName }}</p>
              <div class="private-fund-order__meta">
                <div class="private-fund-order__meta-col">
                  <p class="private-fund-order__amount">{{ item.amount }}</p>
                  <p class="private-fund-order__sub">{{ item.currency }}</p>
                </div>
                <div class="private-fund-order__meta-col is-status">
                  <p
                    class="private-fund-order__status"
                    :class="getOrderStatusClass(item.status)"
                  >
                    {{ item.statusText }}
                  </p>
                  <p class="private-fund-order__sub">{{ item.createdAt }}</p>
                </div>
              </div>
            </div>
          </van-list>
        </template>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
import { List, NavBar, PullRefresh } from "vant";
import { getBrowser } from "@/utils/browser";
import { getAppToken, goBack } from "@/utils/hybirdMethods";
import { getOrderStatusClass, mapFundOrderItem } from "../utils";

const browser = getBrowser();
const PAGE_SIZE = 10;

export default {
  components: {
    [List.name]: List,
    [NavBar.name]: NavBar,
    [PullRefresh.name]: PullRefresh
  },
  data() {
    return {
      list: [],
      loading: false,
      refreshing: false,
      finished: false,
      page: 0,
      ready: false,
      isApp: browser.isApp
    };
  },
  computed: {
    bgColor() {
      return this.$store.getters["common/bgColor"];
    },
    showEmpty() {
      return this.ready && !this.loading && this.finished && this.list.length === 0;
    }
  },
  async created() {
    await this.initAppToken();
    this.ready = true;
    this.onLoad();
  },
  methods: {
    getOrderStatusClass,
    handleBack() {
      if (browser.isApp) {
        goBack();
        return;
      }
      this.$router.go(-1);
    },
    async initAppToken() {
      if (!browser.isApp) {
        return;
      }
      try {
        const res = await getAppToken();
        if (res === "isGetPro" || !res) {
          return;
        }
        const userInfo = JSON.parse(res);
        if (userInfo?.token) {
          localStorage.setItem("token", userInfo.token);
          sessionStorage.setItem("token", userInfo.token);
        }
      } catch (error) {
        console.log("[private-fund/order] 获取 App token 失败:", error);
      }
    },
    async onRefresh() {
      this.page = 0;
      this.list = [];
      this.finished = false;
      try {
        await this.onLoad();
      } finally {
        this.refreshing = false;
      }
    },
    async onLoad() {
      if (!this.ready || this.finished) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.page += 1;
      try {
        const res = await this.$apis.getFundProductOrderList({
          page: this.page,
          page_size: PAGE_SIZE
        });
        const rows = (res?.data || []).map(mapFundOrderItem);
        this.list = this.list.concat(rows);
        const total = Number(res?.total) || 0;
        const currentPage = Number(res?.current_page) || this.page;
        const lastPage = Number(res?.last_page) || currentPage;
        this.finished =
          rows.length === 0 ||
          currentPage >= lastPage ||
          (total > 0 && this.list.length >= total);
      } catch (error) {
        console.log("[private-fund/order] 获取基金订单失败:", error);
        this.finished = true;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
.private-fund-order {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: #fff;

  &__nav {
    flex-shrink: 0;

    /deep/ .van-nav-bar__title {
      font-size: 34px;
      font-weight: 600;
    }
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    /deep/ .van-pull-refresh {
      min-height: 100%;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 150px 0;
  }

  &__empty-icon {
    width: 246px;
    height: 248px;
  }

  &__empty-text {
    margin-top: 14px;
    color: #747377;
    font-size: 28px;
    line-height: 40px;
  }

  &__table-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 28px 16px;
    border-bottom: 1px solid #ebebeb;
  }

  &__th {
    color: #999;
    font-size: 22px;
    line-height: 32px;
    white-space: nowrap;

    &.is-name {
      flex: 1;
      min-width: 0;
    }
  }

  &__th-group {
    display: flex;
    flex-shrink: 0;
    gap: 48px;

    .private-fund-order__th {
      width: 168px;
      text-align: right;
    }
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 28px 28px 24px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__name {
    flex: 1;
    min-width: 0;
    color: #1353c8;
    font-size: 28px;
    line-height: 40px;
    word-break: break-word;
  }

  &__meta {
    display: flex;
    flex-shrink: 0;
    gap: 48px;
  }

  &__meta-col {
    width: 168px;
    text-align: right;

    &.is-status {
      width: 140px;
    }
  }

  &__amount {
    color: #171717;
    font-size: 28px;
    line-height: 40px;
  }

  &__status {
    font-size: 28px;
    line-height: 40px;

    &.is-submitted {
      color: #1353c8;
    }

    &.is-accepted {
      color: #02aa39;
    }

    &.is-rejected {
      color: #e83324;
    }

    &.is-cancelled {
      color: #999;
    }
  }

  &__sub {
    margin-top: 4px;
    color: #999;
    font-size: 22px;
    line-height: 32px;
  }

  &.black-theme {
    background: #0a0b0f;

    .private-fund-order__nav {
      background: #0a0b0f;

      /deep/ .van-nav-bar__title,
      /deep/ .van-icon {
        color: #fff;
      }
    }

    .private-fund-order__table-head,
    .private-fund-order__row {
      border-bottom-color: rgba(255, 255, 255, 0.08);
    }

    .private-fund-order__name {
      color: #377cfb;
    }

    .private-fund-order__amount {
      color: #fff;
    }

    .private-fund-order__th,
    .private-fund-order__sub,
    .private-fund-order__empty-text {
      color: #9d9d9f;
    }

    .private-fund-order__status.is-submitted {
      color: #377cfb;
    }

    /deep/ .van-list__finished-text {
      color: #9d9d9f;
    }
  }
}
</style>
